/**
 * What the Access page and its backend say to each other (DX-342; promised in
 * specs/access-request.md) — one definition both sides compile against, so the
 * panel cannot read a route answer the backend no longer sends, or send a
 * request the backend no longer reads.
 */

/** The eight systems a colleague can ask for access to. */
export const SYSTEM_IDS = [
  'repository',
  'infrastructure',
  'secrets',
  'database',
  'vpn',
  'service-account',
  'team-membership',
  'tool-seat',
] as const;

export type SystemId = (typeof SYSTEM_IDS)[number];

/**
 * Each system's name — on its card, in the panel and in the title of the issue
 * a request opens. One list, so the card a colleague clicked and the issue the
 * approver reads cannot name the system differently.
 */
export const SYSTEM_TITLES: Record<SystemId, string> = {
  repository: 'Repository',
  infrastructure: 'Infrastructure',
  secrets: 'Secrets',
  database: 'Database',
  vpn: 'VPN & SSH',
  'service-account': 'Service account',
  'team-membership': 'Team membership',
  'tool-seat': 'Tool seat',
};

export function isSystemId(value: unknown): value is SystemId {
  return (
    typeof value === 'string' &&
    (SYSTEM_IDS as readonly string[]).includes(value)
  );
}

/**
 * Who answers a request — `GET /api/access/route`, and the `route` of a filed
 * request. The panel shows it before anything is sent; the issue is filed into
 * the same answer.
 */
export type AccessRoute = {
  /** Who answers: the owning team, or Developer Experience. */
  approver: { ref: string; title: string };
  /** The Linear team the issue opens in. */
  linearTeam: { key: string; name: string };
  /**
   * Why the approver is who it is. `owner` is the catalog's answer;
   * `no-owner` a target the catalog names no team for; `undecided` a request
   * nobody has decided an approver for yet.
   */
  reason: 'owner' | 'no-owner' | 'undecided';
  /** For `undecided`: what has no decided approver, in words. */
  undecided?: string;
  /** The target as the colleague knows it. */
  target: { ref?: string; title: string };
};

/** A request as the panel sends it — `POST /api/access/requests`. */
export type AccessRequestBody = {
  system: SystemId;
  /** A catalog ref, or a name — a tool, a new repository as `org/name`. */
  target: string;
  scope?: string;
  duration?: string;
  why: string;
};

/** The answer to a request that was filed. */
export type AccessRequestFiled = {
  issue: { identifier: string; url: string };
  route: AccessRoute;
};

/** The `code` of the 503 a portal without the Linear integration answers. */
export const NOT_CONFIGURED = 'not-configured';

/** The `code` of the 429 a colleague gets past the per-person request limit. */
export const TOO_MANY_REQUESTS = 'too-many-requests';
